import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:util:safe-logger');

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Safe logger that prevents freezing when logging large objects
 * Automatically excludes problematic fields like base64 data
 * Works in both main and renderer processes
 */
export class SafeLogger {
  private static shouldLog(level: LogLevel): boolean {
    // Check if we're in development mode - handle both main and renderer contexts
    let isDev = false;
    try {
      // In renderer process, check globals first, then fallback to process.env
      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        // Check if Vite exposed NODE_ENV as a global
        isDev =
          (globalThis as any).NODE_ENV === 'development' || process.env.NODE_ENV === 'development';
      } else {
        // We're in main process - use NODE_ENV directly
        isDev = process.env.NODE_ENV === 'development';
      }
    } catch {
      // Ultimate fallback
      isDev = process.env.NODE_ENV === 'development';
    }

    // Only log in development or for errors/warnings
    return isDev || level === 'error' || level === 'warn';
  }

  private static sanitizeObject(obj: any, maxDepth = 3, currentDepth = 0): any {
    if (currentDepth >= maxDepth) {
      return '[Max depth reached]';
    }

    if (obj === null || obj === undefined) {
      return obj;
    }

    if (typeof obj === 'string') {
      // For error messages, be less aggressive with truncation
      const maxLength = currentDepth === 0 ? 500 : 100; // Allow longer strings at top level

      // Truncate very long strings (likely base64 data)
      if (obj.length > maxLength) {
        if (obj.startsWith('data:')) {
          return `[Base64 data: ${obj.substring(0, 30)}... (${obj.length} chars)]`;
        }
        // For error messages and stack traces, show more context
        if (obj.includes('Error:') || obj.includes(' at ') || obj.includes('hook')) {
          return `${obj.substring(0, 300)}... (${obj.length} chars total)`;
        }
        return `${obj.substring(0, maxLength)}... (${obj.length} chars)`;
      }
      return obj;
    }

    if (typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      if (obj.length > 10) {
        return [
          ...obj.slice(0, 3).map(item => this.sanitizeObject(item, maxDepth, currentDepth + 1)),
          `... (${obj.length - 6} more items)`,
          ...obj.slice(-3).map(item => this.sanitizeObject(item, maxDepth, currentDepth + 1)),
        ];
      }
      return obj.map(item => this.sanitizeObject(item, maxDepth, currentDepth + 1));
    }

    const result: any = {};
    const keys = Object.keys(obj);

    // Limit number of keys to prevent huge objects
    const maxKeys = 20;
    const keysToProcess = keys.length > maxKeys ? keys.slice(0, maxKeys) : keys;

    for (const key of keysToProcess) {
      // Skip problematic fields that are likely to contain large data
      if (this.shouldSkipField(key)) {
        result[key] = `[Skipped: ${key}]`;
        continue;
      }

      try {
        result[key] = this.sanitizeObject(obj[key], maxDepth, currentDepth + 1);
      } catch (error) {
        result[key] = `[Error serializing ${key}]`;
      }
    }

    if (keys.length > maxKeys) {
      result['...'] = `(${keys.length - maxKeys} more keys)`;
    }

    return result;
  }

  private static shouldSkipField(key: string): boolean {
    const skipFields = [
      'url',
      'dataUrl',
      'base64',
      'data',
      'content',
      'image',
      'screenshot',
      'attachment',
      'file',
    ];

    const lowerKey = key.toLowerCase();
    return skipFields.some(field => lowerKey.includes(field));
  }

  static debug(message: string, data?: any): void {
    if (!this.shouldLog('debug')) return;

    if (data !== undefined) {
      logger.debug(`🐛 ${message}`, this.sanitizeObject(data));
    } else {
      logger.debug(`🐛 ${message}`);
    }
  }

  static info(message: string, data?: any): void {
    if (!this.shouldLog('info')) return;

    if (data !== undefined) {
      logger.info(`ℹ️ ${message}`, this.sanitizeObject(data));
    } else {
      logger.info(`ℹ️ ${message}`);
    }
  }

  static warn(message: string, data?: any): void {
    if (!this.shouldLog('warn')) return;

    if (data !== undefined) {
      logger.warn(`⚠️ ${message}`, this.sanitizeObject(data));
    } else {
      logger.warn(`⚠️ ${message}`);
    }
  }

  static error(message: string, error?: any): void {
    if (!this.shouldLog('error')) return;

    if (error !== undefined) {
      // Special handling for Error objects - provide full context
      if (error instanceof Error) {
        // Log the full error details with expanded context
        const errorDetails = {
          errorName: error.name,
          errorMessage: error.message,
          fullStack: error.stack,
          cause: error.cause,
          // Add React-specific debugging info if available
          componentStack: (error as any).componentStack,
          reactErrorBoundary: (error as any).errorBoundary,
        };

        // Log with full details - don't truncate error messages
        logger.error(`❌ ${message}`);
        logger.error('Error Details:', errorDetails);

        // Also log the full stack trace separately for better visibility
        if (error.stack) {
          logger.error('Full Stack Trace:');
          logger.error(error.stack);
        }

        // Log additional React debugging context if this looks like a React error
        if (error.message && error.message.includes('hook')) {
          logger.error('React Hook Error Context:');
          logger.error(
            '- This error typically occurs when hooks are called outside React components'
          );
          logger.error('- Check that hook calls are not in loops, conditions, or nested functions');
          logger.error('- Ensure hooks are called from React function components or custom hooks');
        }
      } else {
        logger.error(`❌ ${message}`, this.sanitizeObject(error, 5)); // Increase depth for errors
      }
    } else {
      logger.error(`❌ ${message}`);
    }
  }

  /**
   * Safe state logging - useful for debugging component state
   * Automatically sanitizes large objects and base64 data
   */
  static state(componentName: string, state: any): void {
    if (!this.shouldLog('debug')) return;

    logger.debug(`🔍 ${componentName} state:`, this.sanitizeObject(state, 2));
  }

  /**
   * Performance logging - tracks timing
   */
  static perf(operation: string, startTime?: number): void {
    if (!this.shouldLog('debug')) return;

    if (startTime) {
      const duration = performance.now() - startTime;
      logger.debug(`⏱️ ${operation} completed in ${duration.toFixed(2)}ms`);
    } else {
      logger.debug(`⏱️ ${operation} started`);
    }
  }

  /**
   * React-specific error logging with maximum context
   * Use this for React hook errors and component issues
   */
  static reactError(message: string, error?: any, componentContext?: string): void {
    logger.error(`🔴 REACT ERROR: ${message}`);

    if (componentContext) {
      logger.error(`Component Context: ${componentContext}`);
    }

    if (error) {
      // Don't sanitize React errors - we want full context
      if (error instanceof Error) {
        logger.error('Error Object:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
          cause: error.cause,
        });

        // Log the full, untruncated stack trace
        if (error.stack) {
          logger.error('=== FULL STACK TRACE ===');
          logger.error(error.stack);
          logger.error('=== END STACK TRACE ===');
        }
      } else {
        logger.error('Error Value:', error);
      }
    }
  }
}

// Convenience exports - properly bound to maintain context
// Using explicit function declarations to avoid React hook confusion
export function logDebug(message: string, data?: any): void {
  return SafeLogger.debug(message, data);
}

export function logInfo(message: string, data?: any): void {
  return SafeLogger.info(message, data);
}

export function logWarn(message: string, data?: any): void {
  return SafeLogger.warn(message, data);
}

export function logError(message: string, error?: any): void {
  return SafeLogger.error(message, error);
}

export function logState(componentName: string, state: any): void {
  return SafeLogger.state(componentName, state);
}

export function logPerf(operation: string, startTime?: number): void {
  return SafeLogger.perf(operation, startTime);
}

export function logReactError(message: string, error?: any, componentContext?: string): void {
  return SafeLogger.reactError(message, error, componentContext);
}

// Legacy exports for backward compatibility (but these might cause hook issues)
export const debug = logDebug;
export const info = logInfo;
export const warn = logWarn;
export const error = logError;
export const state = logState;
export const perf = logPerf;
export const reactError = logReactError;
